// Master data contracts (Milestone 1, PRD §3).
import { z } from 'zod';
import { isValidGs1Key, normalizeGtin } from '../gs1.js';
import { UOM_CODES } from '../units.js';
import { address } from './admin.js';
import { displayName, entityStatus, pageQuery, uuid } from './common.js';
export const TEMPERATURE_CLASSES = ['ambient', 'chilled', 'frozen'];
export const STORAGE_CLASSES = ['standard', 'bulky', 'high_value', 'hazmat', 'oversize'];
export const ROTATIONS = ['FIFO', 'FEFO', 'LIFO'];
export const LOCATION_TYPES = [
    'rack',
    'shelving',
    'floor',
    'flow_rack',
    'staging',
    'dock',
    'qc',
    'returns',
    'quarantine',
];
export const LPN_TYPES = ['pallet', 'case', 'tote'];
export const PARTNER_TYPES = ['supplier', 'ship_to', 'carrier', 'retailer'];
export const REASON_CATEGORIES = [
    'adjustment',
    'short',
    'damage',
    'return',
    'override',
    'hold',
];
const int = (min, max) => z.coerce.number().int().min(min).max(max);
const optInt = (min, max) => int(min, max).nullable().optional();
export const gtin = z
    .string()
    .trim()
    .transform((v, ctx) => {
    const n = normalizeGtin(v);
    if (!n)
        ctx.addIssue({ code: 'custom', message: 'Not a valid GTIN/UPC (check digit)' });
    return n ?? v;
});
export const gln = z
    .string()
    .trim()
    .refine((v) => isValidGs1Key(v, 13), 'GLN must be 13 digits with a valid check digit');
// ---- Items ----
export const itemUomInput = z.object({
    uom: z.enum(UOM_CODES),
    qtyPerBase: int(1, 1_000_000),
    gtin: gtin.nullable().optional(),
    lengthMm: optInt(0, 100_000),
    widthMm: optInt(0, 100_000),
    heightMm: optInt(0, 100_000),
    weightG: optInt(0, 10_000_000),
    ti: optInt(1, 100),
    hi: optInt(1, 100),
    isPickUom: z.boolean().default(false),
});
const itemFields = {
    description: z.string().trim().min(1).max(200),
    gtin: gtin.nullable().optional(),
    baseUom: z.enum(UOM_CODES).default('EA'),
    storageClass: z.enum(STORAGE_CLASSES).default('standard'),
    temperatureClass: z.enum(TEMPERATURE_CLASSES).default('ambient'),
    hazmatClass: z.string().trim().max(10).nullable().optional(),
    unNumber: z
        .string()
        .trim()
        .regex(/^UN\d{4}$/, 'Format UN1234')
        .nullable()
        .optional(),
    lotControlled: z.boolean().default(false),
    serialControlled: z.boolean().default(false),
    expiryControlled: z.boolean().default(false),
    catchWeight: z.boolean().default(false),
    originRequired: z.boolean().default(false),
    rotation: z.enum(ROTATIONS).default('FIFO'),
    shelfLifeDays: optInt(1, 3650),
    minShipShelfLifeDays: optInt(0, 3650),
    fsmaTraceable: z.boolean().default(false),
    uoms: z.array(itemUomInput).min(1).max(4),
};
function checkItem(v, ctx) {
    if (v.uoms) {
        const codes = v.uoms.map((u) => u.uom);
        if (new Set(codes).size !== codes.length)
            ctx.addIssue({
                code: 'custom',
                path: ['uoms'],
                message: 'Each unit of measure may appear once',
            });
        const base = v.uoms.find((u) => u.uom === v.baseUom);
        if (!base || base.qtyPerBase !== 1)
            ctx.addIssue({
                code: 'custom',
                path: ['uoms'],
                message: 'The base unit must be listed with quantity 1',
            });
    }
    if (v.rotation === 'FEFO' && v.expiryControlled === false)
        ctx.addIssue({
            code: 'custom',
            path: ['rotation'],
            message: 'FEFO needs expiry-controlled items',
        });
    if (v.shelfLifeDays && v.minShipShelfLifeDays && v.minShipShelfLifeDays > v.shelfLifeDays)
        ctx.addIssue({
            code: 'custom',
            path: ['minShipShelfLifeDays'],
            message: 'Cannot exceed the shelf life',
        });
}
export const createItemRequest = z
    .object({
    clientId: uuid,
    sku: z
        .string()
        .trim()
        .regex(/^[A-Za-z0-9][A-Za-z0-9._\-/]{0,39}$/, 'Up to 40 letters, digits or . _ - /'),
    ...itemFields,
})
    .superRefine(checkItem);
export const updateItemRequest = z
    .object({ ...itemFields, status: entityStatus })
    .partial()
    .superRefine(checkItem);
export const itemQuery = pageQuery.extend({
    q: z.string().trim().max(100).optional(),
    clientId: uuid.optional(),
    status: entityStatus.optional(),
    temperatureClass: z.enum(TEMPERATURE_CLASSES).optional(),
});
// ---- Locations ----
const segment = z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9]{1,10}$/, 'Letters and digits only')
    .nullable()
    .optional();
const locationFields = {
    building: segment,
    zone: z
        .string()
        .trim()
        .regex(/^[A-Za-z0-9-]{1,20}$/, 'Letters, digits or -'),
    aisle: segment,
    bay: segment,
    level: segment,
    position: segment,
    type: z.enum(LOCATION_TYPES),
    temperatureZone: z.enum(TEMPERATURE_CLASSES).default('ambient'),
    hazmatZone: z.boolean().default(false),
    pickable: z.boolean().default(true),
    maxWeightG: optInt(0, 100_000_000),
    maxVolumeCm3: optInt(0, 1_000_000_000),
    maxPallets: optInt(0, 100),
    allowMixedSku: z.boolean().default(true),
    allowMixedLot: z.boolean().default(true),
    allowMixedClient: z.boolean().default(false),
    dedicatedClientId: uuid.nullable().optional(),
    pickSequence: int(0, 10_000_000).default(0),
    x: optInt(-1_000_000, 1_000_000),
    y: optInt(-1_000_000, 1_000_000),
};
export const locationCode = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9][A-Z0-9._-]{0,29}$/, 'Up to 30 of A-Z, 0-9, . _ -');
export const createLocationRequest = z.object({
    siteId: uuid,
    code: locationCode,
    ...locationFields,
});
export const updateLocationRequest = z
    .object({ ...locationFields, status: entityStatus })
    .partial();
const range = z
    .object({ from: int(1, 999), to: int(1, 999) })
    .refine((r) => r.to >= r.from, 'End before start');
export const generateLocationsRequest = z
    .object({
    siteId: uuid,
    zone: locationFields.zone,
    aisles: range,
    bays: range,
    levels: z
        .array(z
        .string()
        .trim()
        .regex(/^[A-Z0-9]$/))
        .min(1)
        .max(20),
    positions: range.default({ from: 1, to: 1 }),
    type: z.enum(LOCATION_TYPES),
    temperatureZone: z.enum(TEMPERATURE_CLASSES).default('ambient'),
    hazmatZone: z.boolean().default(false),
    pickableLevels: z.array(z.string()).optional(),
    maxPallets: optInt(0, 100),
    maxWeightG: optInt(0, 100_000_000),
})
    .refine((v) => (v.aisles.to - v.aisles.from + 1) *
    (v.bays.to - v.bays.from + 1) *
    v.levels.length *
    (v.positions.to - v.positions.from + 1) <=
    5000, 'At most 5,000 locations per run');
export const locationQuery = pageQuery.extend({
    q: z.string().trim().max(60).optional(),
    siteId: uuid.optional(),
    zone: z.string().max(20).optional(),
    type: z.enum(LOCATION_TYPES).optional(),
    status: entityStatus.optional(),
});
// ---- LPNs ----
export const generateLpnsRequest = z.object({
    siteId: uuid,
    type: z.enum(LPN_TYPES),
    count: int(1, 500),
});
export const lpnQuery = pageQuery.extend({
    q: z.string().trim().max(40).optional(),
    siteId: uuid.optional(),
    type: z.enum(LPN_TYPES).optional(),
});
// ---- Configuration ----
const configCode = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z][A-Z0-9_]{1,23}$/, '2-24 of A-Z, 0-9, _');
export const inventoryStatusRequest = z.object({
    code: configCode,
    name: displayName,
    allocatable: z.boolean(),
    pickable: z.boolean(),
    isDefault: z.boolean().default(false),
});
export const updateInventoryStatusRequest = inventoryStatusRequest.omit({ code: true }).partial();
export const reasonCodeRequest = z.object({
    code: configCode,
    name: displayName,
    category: z.enum(REASON_CATEGORIES),
    requiresNote: z.boolean().default(false),
});
export const updateReasonCodeRequest = reasonCodeRequest
    .omit({ code: true })
    .partial()
    .extend({ active: z.boolean().optional() });
// ---- Trading partners ----
const partnerFields = {
    name: displayName,
    gln: gln.nullable().optional(),
    scac: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z]{2,4}$/, 'SCAC is 2-4 letters')
        .nullable()
        .optional(),
    address: address.nullable().optional(),
    contactEmail: z.email().max(254).nullable().optional(),
    contactPhone: z.string().trim().max(30).nullable().optional(),
    /** Retailer / ship-to rule: minimum remaining shelf life (days) at delivery. */
    minShelfLifeDays: z.coerce.number().int().min(0).max(3650).nullable().optional(),
};
export const createPartnerRequest = z
    .object({
    type: z.enum(PARTNER_TYPES),
    clientId: uuid.nullable().optional(),
    code: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z0-9][A-Z0-9_-]{0,29}$/, 'Up to 30 of A-Z, 0-9, _ -'),
    ...partnerFields,
})
    .refine((v) => v.type !== 'carrier' || !v.clientId, {
    path: ['clientId'],
    message: 'Carriers are shared across clients',
});
export const updatePartnerRequest = z.object({ ...partnerFields, status: entityStatus }).partial();
export const partnerQuery = pageQuery.extend({
    q: z.string().trim().max(60).optional(),
    type: z.enum(PARTNER_TYPES).optional(),
    clientId: uuid.optional(),
});
// ---- CSV import ----
export const importRequest = z.object({
    csv: z.string().min(1).max(15_000_000),
    dryRun: z.boolean().default(true),
    /** Items: owning client. Locations: site. */
    clientId: uuid.optional(),
    siteId: uuid.optional(),
});
//# sourceMappingURL=master.js.map